// Presentational pieces for the bank data screens (Financials v2, slice 1):
// data in, markup out. No fetching hooks and no Firebase import, so
// BankParts.test.jsx renders them with renderToStaticMarkup and fixtures.
// The containers live in BankDataCard.jsx (Settings) and UnmatchedLists.jsx
// (the lists behind a row); Overview.jsx and Expenses.jsx wire these in.
// Every figure comes from a bank_status / bank_unmatched / get_accounts /
// bankUploadStatement field (lib/bankData.js does the shaping).
import React from "react";
import { C, btnP, btnS, font, inp, lbl, mono, sel } from "../../../lib/styles.js";
import { Kv, PanelHd, PanelNote, lblStyle, linkBtn, money, panel, td, th } from "./common.jsx";
import {
  acctName, balanceText, describeUpload, fmtDay, fmtDayYear, fmtRange, fmtWhen, formatName,
  kindName, localDay, plural, statementPhrase, truncate, withDays,
} from "../lib/bankData.js";

const num = { fontFamily: mono, fontVariantNumeric: "tabular-nums" };
const thR = { ...th, textAlign: "right" };
const tdAmt = { ...td, ...num, textAlign: "right", whiteSpace: "nowrap" };
const pad = { padding: "10px 14px 12px", fontSize: 12.5, fontFamily: font };
const sectionTop = { borderTop: `1px solid ${C.border}` };
const mutedLine = { fontSize: 12.5, color: C.muted, lineHeight: 1.5 };
const attnLine = { fontSize: 12.5, color: C.capture, lineHeight: 1.5 };
const subLine = (attn) => ({ display: "block", fontSize: 11.5, color: attn ? C.capture : C.muted, marginTop: 2 });
const off = { opacity: 0.45, cursor: "default" };
const field = { ...inp, padding: "8px 11px" };
const selField = { ...sel, padding: "8px 30px 8px 11px" };
const box = {
  marginTop: 10, padding: "12px 14px", background: C.bg,
  border: `1px solid ${C.border}`, borderRadius: 6,
};
const cap = (s) => (s ? s[0].toUpperCase() + s.slice(1) : s);

const TextAction = ({ label, onClick, disabled, style }) => (
  <button type="button" style={{ ...linkBtn, ...(disabled ? off : null), ...style }}
    onClick={onClick} disabled={disabled}>{label}</button>
);

/** An error from a fetch, said as an error, with a retry, inside the piece that failed. */
export const FetchError = ({ what, error, onRetry }) => (
  <div style={attnLine}>
    {what} could not be read: {error?.message || "unknown error"}
    {onRetry ? <>{" "}<TextAction label="Retry" onClick={onRetry} /></> : null}
  </div>
);

// ── Overview: attention row ──────────────────────────────────────────────────

/** The words of lib/bankData.js attentionText, with the amount in DM Mono. */
export const BankAttentionText = ({ attention }) => {
  if (!attention) return null;
  const { notInBooks, worst } = attention;
  const from = `from the ${statementPhrase(attention.asOf, attention.datesDiffer)}`;
  const amt = worst ? <span style={{ ...num, fontSize: 11.5 }}>{money(worst.diff)}</span> : null;
  if (notInBooks > 0) {
    return (
      <>
        <b style={{ fontWeight: 400 }}>{plural(notInBooks, "bank transaction", "bank transactions")} not in QuickBooks yet</b>
        {` · ${from}`}
        {worst ? <>{` · ${acctName(worst.label, worst.last4)} differs by `}{amt}</> : null}
      </>
    );
  }
  return (
    <>
      <b style={{ fontWeight: 400 }}>{`${acctName(worst?.label, worst?.last4)} differs from QuickBooks by `}</b>
      {amt}
      {` · ${from}`}
    </>
  );
};

/** One quiet Attn item (grey dot) for the Overview; "Details" expands the accounts behind it. */
export const bankAttentionItem = (attention, onDetails) => ({
  text: <BankAttentionText attention={attention} />,
  quiet: true,
  action: onDetails ? { label: "Details", onClick: onDetails } : undefined,
});

// ── Overview: Bank vs QuickBooks ─────────────────────────────────────────────

/**
 * rows from bankOverview. A row with a comparison is clickable and expands to
 * its note and renderDetails(row) (the unmatched lists). openIds: the
 * accountIds expanded.
 */
export function BankVsBooksTable({ rows, latestAsOf, openIds = [], onToggle, renderDetails }) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={th}>Account</th><th style={thR}>Bank</th><th style={thR}>Books</th><th style={thR}>Diff</th>
          </tr>
        </thead>
        <tbody>
          {(rows ?? []).map((r) => {
            const open = r.hasCompare && openIds.includes(r.accountId);
            const toggle = r.hasCompare && onToggle ? () => onToggle(r.accountId) : undefined;
            // A row whose statement is not the panel's date says its own.
            const own = r.asOf && latestAsOf && r.asOf !== latestAsOf ? ` · statement of ${fmtDay(r.asOf)}` : "";
            const diffColor = r.diff === null || r.diff === 0 ? C.muted : C.capture;
            return (
              <React.Fragment key={r.accountId}>
                <tr
                  onClick={toggle}
                  onKeyDown={toggle ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(); } } : undefined}
                  tabIndex={toggle ? 0 : undefined}
                  aria-expanded={r.hasCompare ? open : undefined}
                  style={{ cursor: toggle ? "pointer" : "default", background: open ? C.bg : undefined }}
                >
                  <td style={{ ...td, whiteSpace: "normal" }}>
                    {acctName(r.label, r.last4)}
                    <span style={subLine(r.subAttn)}>{r.sub}{own}</span>
                    {r.hasCompare && r.error ? (
                      <span style={subLine(true)}>Last comparison attempt failed: {r.error}</span>
                    ) : null}
                  </td>
                  <td style={tdAmt}>{money(r.bank, false)}</td>
                  <td style={tdAmt}>{money(r.books, false)}</td>
                  <td style={{ ...tdAmt, color: diffColor }}>{r.diff === null ? "—" : money(r.diff)}</td>
                </tr>
                {open ? (
                  <tr>
                    <td colSpan={4} style={{ ...td, background: C.bg, whiteSpace: "normal", padding: "10px 12px 14px" }}>
                      {r.note ? <div style={{ ...mutedLine, fontSize: 12, marginBottom: 10 }}>{r.note}</div> : null}
                      {renderDetails ? renderDetails(r) : null}
                    </td>
                  </tr>
                ) : null}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The Overview panel. bank: the useQbo state of bank_status; overview:
 * bankOverview(bank.data), or null while there is no data. An error shows
 * here, in this panel only.
 */
export function BankVsBooksPanel({ bank, overview, openIds, onToggle, onGoSettings, onRetry, renderDetails, style }) {
  let body;
  if (bank?.error) {
    body = <div style={pad}><FetchError what="Bank data" error={bank.error} onRetry={onRetry} /></div>;
  } else if (!overview) {
    body = <div style={{ ...pad, color: C.muted }}>Loading bank data…</div>;
  } else if (!overview.hasRows) {
    body = (
      <div style={{ ...pad, display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
        <span style={{ color: C.muted }}>No bank data yet.</span>
        {onGoSettings ? <TextAction label="Upload a statement in Settings" onClick={onGoSettings} /> : null}
      </div>
    );
  } else {
    body = (
      <BankVsBooksTable rows={overview.rows} latestAsOf={overview.latestAsOf}
        openIds={openIds} onToggle={onToggle} renderDetails={renderDetails} />
    );
  }
  return (
    <div style={{ ...panel, padding: 0, ...style }}>
      <PanelHd title="Bank vs QuickBooks"
        note={overview?.hasData ? statementPhrase(overview.latestAsOf, overview.datesDiffer) : null} />
      {body}
      <PanelNote>
        Bank figures come from the statements you upload, never from QuickBooks. Balances are compared as of the
        file&apos;s own dates.
      </PanelNote>
    </div>
  );
}

// ── The lists behind a row (bank_unmatched) ──────────────────────────────────

const listCell = { padding: "5px 10px 5px 0", borderBottom: `1px solid ${C.border}`, fontSize: 12.5, verticalAlign: "top" };

const TxnList = ({ title, caption, rows, expected, amountOf }) => (
  <div>
    <div style={{ display: "flex", gap: 8, alignItems: "baseline", marginBottom: 4, flexWrap: "wrap" }}>
      <span style={lblStyle}>{title}</span>
      {caption ? <span style={{ fontSize: 11.5, color: C.muted }}>{caption}</span> : null}
    </div>
    {rows.length === 0 ? <div style={mutedLine}>None.</div> : (
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.key}|${i}`}>
              <td style={{ ...listCell, ...num, fontSize: 11.5, color: C.muted, whiteSpace: "nowrap", width: 56 }}>{fmtDay(r.date)}</td>
              <td style={listCell}>{r.text}</td>
              <td style={{ ...listCell, ...num, textAlign: "right", whiteSpace: "nowrap", paddingRight: 0, color: amountOf(r.amount).color }}>
                {amountOf(r.amount).text}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
    {typeof expected === "number" && rows.length < expected ? (
      <div style={{ ...mutedLine, fontSize: 11.5, marginTop: 4 }}>Showing {rows.length} of {expected}.</div>
    ) : null}
  </div>
);

// Bank side: the bank's sign, money in green. QuickBooks side: as QuickBooks
// prints it (signed by transaction type), so no direction is implied.
const bankAmount = (n) => ({ text: money(n), color: typeof n === "number" && n > 0 ? C.green : C.text });
const booksAmount = (n) => ({ text: money(n), color: C.text });

/**
 * bank_unmatched → "Not in QuickBooks" (bank transactions) and "Not cleared
 * at the bank" (QuickBooks transactions). only="notInBooks" drops the second
 * list (the Expenses bank-feed line). expected: the row's counts, so a list
 * cut short by the server says so.
 */
export function UnmatchedListsView({ data, loading, error, onRetry, only, expected }) {
  if (error) return <FetchError what="The transactions" error={error} onRetry={onRetry} />;
  if (!data) return <div style={mutedLine}>{loading ? "Loading the transactions…" : "Nothing loaded."}</div>;
  // No comparison stored (remapped since this page loaded): empty lists would read as "all booked".
  if (!data.asOf) return <div style={mutedLine}>No comparison is stored for this account now. The next upload compares it.</div>;
  const notInBooks = (data.notInBooks ?? []).map((t) => ({
    key: t.txnId, date: t.date, amount: t.amount,
    text: [t.name, t.category].filter(Boolean).join(" · "),
  }));
  const notAtBank = (data.notAtBank ?? []).map((q) => ({
    key: q.key, date: q.date, amount: q.amount,
    text: [q.type, [q.name, q.docNum ? `#${q.docNum}` : null].filter(Boolean).join(" ")].filter(Boolean).join(" · "),
  }));
  return (
    <div style={{ display: "grid", gap: 12 }}>
      <TxnList title="Not in QuickBooks" caption="from the bank file" rows={notInBooks}
        expected={expected?.notInBooks} amountOf={bankAmount} />
      {only === "notInBooks" ? null : (
        <TxnList title="Not cleared at the bank" caption="in QuickBooks, amounts as it prints them" rows={notAtBank}
          expected={expected?.notAtBank} amountOf={booksAmount} />
      )}
    </div>
  );
}

// ── Expenses: the bank-feed line ─────────────────────────────────────────────

/**
 * Under the expenses table. bank: the useQbo state of bank_status; overview:
 * bankOverview(bank.data) or null. With bank data: the not-in-QuickBooks
 * count and a Show action that expands renderAccount(row) for each account
 * that has any. Without: the way to get it (Settings). An error stays in
 * this line.
 */
export function BankFeedLine({ bank, overview, open, onToggle, onGoSettings, onRetry, renderAccount }) {
  const lead = "Still in the bank feed, not in QuickBooks";
  let line;
  let action = null;
  if (bank?.error) {
    line = <span style={{ color: C.capture }}>{lead} · could not be read: {bank.error.message || "unknown error"}</span>;
    if (onRetry) action = { label: "Retry", onClick: onRetry };
  } else if (!overview) {
    line = <span style={{ color: C.muted }}>{lead} · loading…</span>;
  } else if (overview.hasData) {
    const n = overview.notInBooksTotal;
    line = (
      <span>
        {`${lead} · `}
        <span style={{ color: n > 0 ? C.capture : C.muted }}>
          <span style={num}>{n}</span>{n === 1 ? " transaction" : " transactions"}
        </span>
        {` · from the ${statementPhrase(overview.latestAsOf, overview.datesDiffer)}`}
      </span>
    );
    if (n > 0 && onToggle) action = { label: open ? "Hide" : "Show", onClick: onToggle };
  } else if (overview.rows.some((r) => r.error)) {
    // Uploaded, but no comparison has landed: say why instead of "upload one".
    const r = overview.rows.find((x) => x.error);
    line = (
      <span style={{ color: C.capture }}>
        {acctName(r.label, r.last4)}: a statement is on file but was not compared with QuickBooks. {r.error}
      </span>
    );
    if (onGoSettings) action = { label: "Open Settings", onClick: onGoSettings };
  } else {
    line = <span style={{ color: C.muted }}>Upload a statement in Settings to see what is still in the bank feed.</span>;
    if (onGoSettings) action = { label: "Open Settings", onClick: onGoSettings };
  }
  const accounts = open && overview?.hasData ? overview.rows.filter((r) => r.notInBooks > 0) : [];
  return (
    <div style={{ ...panel, padding: 0, marginTop: 12 }}>
      <div style={{ display: "flex", gap: 10, alignItems: "baseline", padding: "10px 14px", fontSize: 12.5 }}>
        <span style={{ minWidth: 0, lineHeight: 1.5 }}>{line}</span>
        {action ? <TextAction label={action.label} onClick={action.onClick} style={{ marginLeft: "auto" }} /> : null}
      </div>
      {accounts.length ? (
        <div style={{ ...sectionTop, padding: "10px 14px 14px", display: "grid", gap: 16 }}>
          {accounts.map((r) => (
            <div key={r.accountId}>
              <div style={{ fontSize: 13, marginBottom: 6 }}>{acctName(r.label, r.last4)}</div>
              {renderAccount ? renderAccount(r) : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// ── Settings: mapped accounts ────────────────────────────────────────────────

/** bank_status accounts, one line each, with a "Change" action; editForm renders under the one being edited. */
export function MappedAccounts({ accounts, editId, editForm, onChange, disabled }) {
  if (!accounts?.length) {
    return (
      <div style={{ ...pad, ...mutedLine }}>
        No accounts mapped yet. The first upload of each account asks which QuickBooks account it is.
      </div>
    );
  }
  return (
    <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
      {accounts.map((a, i) => {
        const facts = [
          a.kind === "card" ? "card" : "bank",
          `→ ${a.qboAccountName}`,
          a.coverageStart ? `bank data since ${fmtDay(a.coverageStart)}` : null,
          a.lastUploadedAt ? `last upload ${fmtDayYear(localDay(a.lastUploadedAt))}` : "no upload yet",
        ].filter(Boolean).join(" · ");
        return (
          <li key={a.accountId} style={{ padding: "9px 14px", ...(i ? sectionTop : null) }}>
            <div style={{ display: "flex", gap: 10, alignItems: "baseline" }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 13 }}>{acctName(a.label, a.last4)}</div>
                <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.5 }}>{facts}</div>
                {a.lastCompareError ? (
                  <div style={{ fontSize: 11.5, color: C.capture, lineHeight: 1.5 }}>{withDays(a.lastCompareError)}</div>
                ) : null}
              </div>
              {onChange ? (
                <TextAction label="Change" onClick={() => onChange(a)} disabled={disabled || editId === a.accountId} />
              ) : null}
            </div>
            {editId === a.accountId ? editForm : null}
          </li>
        );
      })}
    </ul>
  );
}

// ── Settings: mapping ────────────────────────────────────────────────────────

const optionText = (a) => [
  a.name,
  typeof a.currentBalance === "number" ? money(a.currentBalance) : null,
  a.current ? "current mapping" : null,
].filter(Boolean).join(" · ");

/**
 * Label + QuickBooks account picker. choices: { list, loading, error, onRetry }
 * (list: get_accounts accounts of the right type, see accountsForKind).
 */
export function MapFields({ kind, label, onLabel, choices, qboAccountId, onPick, disabled }) {
  const list = choices?.list ?? [];
  const typeName = kind === "card" ? "Credit Card" : "Bank";
  return (
    <div style={{ display: "grid", gap: 10 }}>
      <label style={{ display: "block" }}>
        <span style={lbl}>Label</span>
        <input style={field} value={label} maxLength={60} disabled={disabled}
          onChange={(e) => onLabel(e.target.value)} />
      </label>
      <div>
        <span style={lbl}>QuickBooks account</span>
        {choices?.error ? <FetchError what="QuickBooks accounts" error={choices.error} onRetry={choices.onRetry} /> : null}
        {choices?.loading ? <div style={mutedLine}>Loading QuickBooks accounts…</div> : null}
        {!choices?.loading && !choices?.error && !list.length ? (
          <div style={mutedLine}>QuickBooks has no active {typeName} account to map this to.</div>
        ) : null}
        {list.length ? (
          <select style={selField} value={qboAccountId} disabled={disabled} aria-label="QuickBooks account"
            onChange={(e) => onPick(e.target.value)}>
            <option value="">Choose a {typeName} account…</option>
            {list.map((a) => <option key={a.id} value={a.id}>{optionText(a)}</option>)}
          </select>
        ) : null}
      </div>
    </div>
  );
}

const Buttons = ({ children }) => (
  <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 12, flexWrap: "wrap" }}>{children}</div>
);
const Btn = ({ primary, disabled, onClick, children }) => (
  <button type="button" style={{ ...(primary ? btnP : btnS), ...(disabled ? off : null) }}
    disabled={disabled} onClick={onClick}>{children}</button>
);
const InlineError = ({ children }) => (children ? <div style={{ ...attnLine, marginTop: 8 }}>{children}</div> : null);

/** The "Change" form for a mapped account. Saving calls bankSetAccount. */
export function RemapForm({ account, label, onLabel, choices, qboAccountId, onPick, remaps, busy, error, onSave, onCancel }) {
  const canSave = Boolean(label.trim() && qboAccountId && !busy);
  return (
    <div style={box}>
      <div style={{ fontSize: 12, color: remaps ? C.capture : C.muted, lineHeight: 1.5, marginBottom: 10 }}>
        Changing the QuickBooks account drops the stored comparison for {acctName(account.label, account.last4)}; the
        next upload compares it again.
      </div>
      <MapFields kind={account.kind} label={label} onLabel={onLabel} choices={choices}
        qboAccountId={qboAccountId} onPick={onPick} disabled={busy} />
      <Buttons>
        <Btn primary disabled={!canSave} onClick={onSave}>{busy ? "Saving…" : "Save"}</Btn>
        <Btn disabled={busy} onClick={onCancel}>Cancel</Btn>
      </Buttons>
      <InlineError>{error}</InlineError>
    </div>
  );
}

/**
 * The prompt a needsAccount upload opens. reason "unmapped": map the
 * account (label + QuickBooks account), then the same file is stored.
 * reason "no_account_in_file" (a CSV): pick the mapped account it belongs to
 * (mapped: { list, loading, error, onRetry }); with none, only Cancel.
 * busy: null | "saving" | "storing".
 */
export function MappingPrompt({
  reason, summary, fileName, label, onLabel, choices, qboAccountId, onPickQbo,
  mapped, accountId, onPickAccount, busy, error, onSave, onStore, onCancel,
}) {
  const s = summary ?? {};
  const balance = balanceText(s.balances?.ledger ?? s.balances?.ending ?? null) ?? "None in the file";
  const facts = [
    { k: "File", v: `${fileName ? `${truncate(fileName, 40)} · ` : ""}${formatName(s.format)}` },
    { k: "Account", v: s.last4 ? `${cap(kindName(s.kind))} ··${s.last4}` : `${cap(kindName(s.kind))} · no account number in the file` },
    { k: "Covers", v: `${fmtRange(s.range)} · ${plural(s.txnCount ?? 0, "transaction", "transactions")}` },
    { k: "Balance", v: balance },
  ];
  const warnings = (s.warnings ?? []).filter(Boolean);
  const unmapped = reason === "unmapped";
  const list = mapped?.list ?? [];
  let body;
  if (unmapped) {
    const canSave = Boolean(label?.trim() && qboAccountId && !busy);
    body = (
      <>
        <MapFields kind={s.kind} label={label ?? ""} onLabel={onLabel} choices={choices}
          qboAccountId={qboAccountId} onPick={onPickQbo} disabled={Boolean(busy)} />
        <Buttons>
          <Btn primary disabled={!canSave} onClick={onSave}>
            {busy === "saving" ? "Saving…" : busy === "storing" ? "Storing…" : "Save and store"}
          </Btn>
          <Btn disabled={Boolean(busy)} onClick={onCancel}>Cancel</Btn>
        </Buttons>
      </>
    );
  } else if (mapped?.error) {
    body = (
      <>
        <FetchError what="The mapped accounts" error={mapped.error} onRetry={mapped.onRetry} />
        <Buttons><Btn onClick={onCancel}>Cancel</Btn></Buttons>
      </>
    );
  } else if (mapped?.loading && !list.length) {
    body = (
      <>
        <div style={mutedLine}>Loading the mapped accounts…</div>
        <Buttons><Btn onClick={onCancel}>Cancel</Btn></Buttons>
      </>
    );
  } else if (!list.length) {
    body = (
      <>
        <div style={mutedLine}>
          This CSV carries no account number. Upload this account&apos;s QFX export once (Bank of America&apos;s
          {" "}“WEB Connect for QuickBooks”), which does; after that, its CSVs can be stored against it.
        </div>
        <Buttons><Btn onClick={onCancel}>Cancel</Btn></Buttons>
      </>
    );
  } else {
    body = (
      <>
        <div style={{ ...mutedLine, marginBottom: 8 }}>
          This CSV carries no account number, so nothing is stored until you pick the account it belongs to.
        </div>
        <span style={lbl}>Mapped account</span>
        <select style={selField} value={accountId} disabled={Boolean(busy)} aria-label="Mapped account"
          onChange={(e) => onPickAccount(e.target.value)}>
          <option value="">Choose an account…</option>
          {list.map((a) => (
            <option key={a.accountId} value={a.accountId}>{`${acctName(a.label, a.last4)} → ${a.qboAccountName}`}</option>
          ))}
        </select>
        <Buttons>
          <Btn primary disabled={!accountId || Boolean(busy)} onClick={onStore}>{busy === "storing" ? "Storing…" : "Store"}</Btn>
          <Btn disabled={Boolean(busy)} onClick={onCancel}>Cancel</Btn>
        </Buttons>
      </>
    );
  }
  return (
    <div style={box}>
      <div style={{ fontSize: 13, marginBottom: 2 }}>
        {unmapped ? "Which QuickBooks account is this?" : "Which account is this CSV for?"}
      </div>
      {unmapped ? (
        <div style={{ ...mutedLine, fontSize: 12 }}>
          This {kindName(s.kind)} ending {s.last4} is not mapped yet, so nothing has been stored.
        </div>
      ) : null}
      <div style={{ margin: "4px -14px 4px" }}><Kv text rows={facts} /></div>
      {warnings.length ? (
        <ul style={{ margin: "0 0 10px", padding: "0 0 0 16px", fontSize: 11.5, color: C.muted, lineHeight: 1.5 }}>
          {warnings.map((w, i) => <li key={i}>{w}</li>)}
        </ul>
      ) : null}
      {body}
      <InlineError>{error}</InlineError>
    </div>
  );
}

// ── Settings: after an upload ────────────────────────────────────────────────

/** An ok bankUploadStatement result (lib/bankData.js describeUpload). */
export function UploadResult({ result, fileName, accountName }) {
  const d = describeUpload(result);
  let compare;
  if (d.compare) compare = { v: d.compare };
  else if (d.compareError) compare = { v: d.compareError, tone: "attn" };
  else compare = { v: "Not compared", tone: "dim" };
  return (
    <div style={box}>
      <div style={{ fontSize: 13 }}>
        Stored{accountName ? ` against ${accountName}` : ""}
        {fileName ? <span style={{ color: C.muted, fontSize: 12 }}>{` · ${truncate(fileName, 40)}`}</span> : null}
      </div>
      <div style={{ margin: "4px -14px 0" }}>
        <Kv text rows={[
          { k: "Transactions", v: d.stored },
          { k: "Balance", v: d.balance },
          { k: "QuickBooks", v: compare.v, tone: compare.tone },
        ]} />
      </div>
      {d.note ? <div style={{ ...mutedLine, fontSize: 12 }}>{d.note}</div> : null}
      {d.warnings.length ? (
        <ul style={{ margin: "6px 0 0", padding: "0 0 0 16px", fontSize: 11.5, color: C.muted, lineHeight: 1.5 }}>
          {d.warnings.map((w, i) => <li key={i}>{w}</li>)}
        </ul>
      ) : null}
    </div>
  );
}

// ── Settings: recent uploads ─────────────────────────────────────────────────

const thS = { ...th, padding: "7px 8px" };
const tdS = { ...td, padding: "7px 8px", fontSize: 12, whiteSpace: "nowrap" };
// The file column takes whatever width the others leave and ends in an
// ellipsis there (the card is 556px wide); the full name is its tooltip.
const fileCol = { width: "100%", maxWidth: 0, overflow: "hidden", textOverflow: "ellipsis" };

/** bank_status recentUploads (newest first); accounts names them. Nothing when there are none. */
export function RecentUploadsTable({ uploads, accounts }) {
  if (!uploads?.length) return null;
  const nameOf = new Map((accounts ?? []).map((a) => [a.accountId, acctName(a.label, a.last4)]));
  return (
    <div style={sectionTop}>
      <div style={{ padding: "10px 14px 2px" }}><span style={lblStyle}>Recent uploads</span></div>
      <div style={{ overflowX: "auto", padding: "0 6px" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={thS}>When</th><th style={{ ...thS, ...fileCol }}>File</th><th style={thS}>Account</th>
              <th style={thS}>Range</th><th style={{ ...thS, textAlign: "right" }}>Transactions</th>
            </tr>
          </thead>
          <tbody>
            {uploads.map((u) => (
              <tr key={u.statementId}>
                <td style={{ ...tdS, ...num, color: C.muted, fontSize: 11.5 }}>{fmtWhen(u.uploadedAt)}</td>
                <td style={{ ...tdS, ...fileCol }} title={u.fileName}>{truncate(u.fileName, 40)}</td>
                <td style={tdS}>{nameOf.get(u.accountId) ?? u.accountId}</td>
                <td style={{ ...tdS, ...num, color: C.muted, fontSize: 11.5 }}>{fmtRange(u.range)}</td>
                <td style={{ ...tdS, ...num, textAlign: "right" }}>{`${u.txnCount ?? 0} · ${u.newCount ?? 0} new`}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
